import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { TableFooter, TablePagination, TableSortLabel } from "@mui/material";
import TablePaginationActions from "@mui/material/TablePagination/TablePaginationActions";

interface RowData<T> {
    [key: string]: T;
}

interface TableProps<T extends React.ReactNode> {
    headerData: string[];
    headerColumnWidth?: number[];
    rowsData: RowData<T>[];
    rowsPerPage?: number;
    page?: number;
    count?: number;
    handleChangePage?: any;
    handleChangeRowsPerPage?: any;
    isTableFooter?: boolean;
    handleSort?: any;
    orderBy?: string | null;
    order?: any;
    sortableFields?: any;
}

const DefaultTable = <T extends React.ReactNode>({
    headerData,
    headerColumnWidth,
    rowsData,
    rowsPerPage = 20,
    page = 0,
    count,
    handleChangePage,
    handleChangeRowsPerPage,
    isTableFooter = true,
    handleSort,
    orderBy,
    order,
    sortableFields,
}: TableProps<T>) => {
    return (
        <TableContainer component={Paper} sx={{ mb: "30px" }}>
            <Table sx={{ minWidth: 650 }} size="small" aria-label="sortable table">
                <TableHead>
                    <TableRow>
                        {headerData.map((header, index) => (
                            <TableCell
                                key={`${header}-${index}`}
                                sortDirection={orderBy === header ? order : false}
                                sx={{ width: headerColumnWidth && `${headerColumnWidth[index]}%`, fontWeight: 600 }}
                            >
                                {!sortableFields?.[header] ? (
                                    header // Display plain text if sorting is disabled
                                ) : (
                                    <TableSortLabel
                                        active={orderBy === header}
                                        direction={orderBy === header ? order : "ASC"}
                                        onClick={() => handleSort?.(header)}
                                    >
                                        {header}
                                    </TableSortLabel>
                                )}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rowsData.map((row, index) => (
                        <TableRow
                            key={index}
                            sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                        >
                            {Object.values(row).map((value, innerIndex) => (
                                <TableCell key={innerIndex}>{value}</TableCell>
                            ))}
                        </TableRow>
                    ))}
                    {!rowsData.length && (
                        <TableRow
                            sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                        >
                            <TableCell
                                colSpan={headerData.length || 5}
                                sx={{ textAlign: "center", fontSize: 24, padding: 10 }}
                            >
                                <i>No Data</i>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
                {isTableFooter && rowsData.length > 0 && (
                    <TableFooter>
                        <TableRow>
                            <TablePagination
                                rowsPerPageOptions={[20, 50, { label: "All", value: -1 }]}
                                colSpan={headerData?.length}
                                count={count || rowsData.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                slotProps={{
                                    select: {
                                        inputProps: {
                                            "aria-label": "rows per page",
                                        },
                                        native: true,
                                    },
                                }}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                ActionsComponent={TablePaginationActions}
                            />
                        </TableRow>
                    </TableFooter>
                )}
            </Table>
        </TableContainer>
    );
};

export default DefaultTable;
